import React from "react";
import CustomerPaymentBarSection from "./sections/CustomerPaymentBarSection";
import { useQuery } from "@tanstack/react-query";
import { paymentsFetch } from "@/src/lib/paymentsFetch";
import PaymentSchedulesSection from "./sections/PaymentSchedulesSection";
import { Booking, Operator } from "../model";
import CustomerPaymentSection from "./sections/CustomerPaymentSection";
import OperatorPaymentSection from "./sections/OperatorPaymentSection";
import { fetch } from "@/src/lib/fetch";
import type { PaymentEmailRow } from "../PaymentEmailLog";

type CurrencyCode = "EUR" | "GBP" | "USD" | "CAD" | "AUD" | "NZD";

export const Payments = ({
  booking,
  handleCopyToClipboard,
}: {
  booking: Booking;
  handleCopyToClipboard: (text: string) => void;
}) => {
  const { data: paymentScheduleCustomerData, isLoading: isLoadingPaymentScheduleCustomer } = useQuery({
    queryKey: ["paymentScheduleCustomerData", booking.id],
    queryFn: () =>
      paymentsFetch<{ data: any[] }>(
        `/api/payment-schedule-step?page=1&limit=10&orderBy=Booking+date&payerType=Customer&bookingRef=${booking.id}&operatorRef=null`,
      ).then((res) => res),
    enabled: !!booking.id,
  });

  const { data: paymentScheduleOperatorData, isLoading: isLoadingPaymentScheduleOperator } = useQuery({
    queryKey: ["paymentScheduleOperatorData", booking.id],
    queryFn: () =>
      paymentsFetch<{ data: any[] }>(
        `/api/payment-schedule-step?page=1&limit=10&orderBy=Booking+date&payerType=Operator&bookingRef=${booking.id}&operatorRef=null`,
      ).then((res) => res),
    enabled: !!booking.id,
  });

  const { data: paymentsData, isLoading: isLoadingPayments } = useQuery({
    queryKey: ["paymentsData", booking.id],
    queryFn: () => paymentsFetch<any[]>(`/api/payments/booking/${booking.id}`).then((res) => res),
    enabled: !!booking.id,
  });

  // Emails tied to each payment (automated confirmations, receipts, remittances).
  const { data: paymentEmails, isLoading: isLoadingPaymentEmails } = useQuery({
    queryKey: ["paymentEmails", booking.id],
    queryFn: () => fetch<{ data: Record<string, PaymentEmailRow[]> }>(`/api/bookings/${booking.id}/payment-emails`).then((r) => r.data),
    enabled: !!booking.id,
  });

  const { data: operatorData, isLoading: isLoadingOperator } = useQuery({
    queryKey: ["operatorData", booking.offerYacht?.yacht?.id],
    queryFn: () =>
      fetch<{ operator: Operator }>(`/api/operators/by-yacht/${booking.offerYacht?.yacht?.id}`).then((res) => res),
    enabled: !!booking.offerYacht?.yacht?.id,
  });

  const bookingYachtCurrencyCode = booking?.pricing?.yachtCurrencyCode as CurrencyCode;
  const bookingOperatorCurrencyCode = booking.operatorTransfer?.currencyCode as CurrencyCode;
  const operatorCurrencyCode =
    bookingOperatorCurrencyCode ||
    bookingYachtCurrencyCode ||
    operatorData?.operator?.transferDetails?.currencyCode ||
    "EUR";

  const customerPaymentData =
    paymentsData
      ?.filter((payment) => payment.payerType === "Customer")
      ?.sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()) || [];

  const operatorPaymentData =
    paymentsData
      ?.filter((payment) => payment.payerType === "Operator")
      ?.sort((a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()) || [];

  const customerBookingAmount = +paymentScheduleCustomerData?.data?.[0]?.bookingAmount;
  const customerPaidSoFar = paymentScheduleCustomerData?.data?.reduce(
    (acc: number, item: { paidSoFar: number }) => acc + item.paidSoFar,
    0,
  );

  const operatorBookingAmount = +paymentScheduleOperatorData?.data?.reduce(
    (acc: number, item: { amount: string }) => acc + +item.amount,
    0,
  );
  const operatorPaidSoFar = paymentScheduleOperatorData?.data?.reduce(
    (acc: number, item: { paidSoFar: number }) => acc + item.paidSoFar,
    0,
  );

  return (
    <React.Fragment>
      <>
        <CustomerPaymentBarSection paymentScheduleData={paymentScheduleCustomerData} />
        <PaymentSchedulesSection
          paymentScheduleCustomerData={paymentScheduleCustomerData}
          paymentScheduleOperatorData={paymentScheduleOperatorData}
          currencyCode={booking?.currency?.code}
          operatorCurrencyCode={operatorCurrencyCode}
          bookingId={booking.id}
          initialAcceptCardPayments={booking?.acceptCardPayments}
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
          <div className="w-full">
            <CustomerPaymentSection
              customerPaymentData={customerPaymentData}
              currencyCode={booking?.currency?.code}
              bookingId={booking.id}
              paymentData={{ bookingAmount: customerBookingAmount, paidSoFar: customerPaidSoFar }}
              emails={paymentEmails}
              emailsLoading={isLoadingPaymentEmails}
            />
          </div>
          <div className="w-full">
            <OperatorPaymentSection
              operatorPaymentData={operatorPaymentData}
              currencyCode={booking?.currency?.code}
              operatorCurrencyCode={operatorCurrencyCode}
              bookingId={booking.id}
              paymentData={{ bookingAmount: operatorBookingAmount, paidSoFar: operatorPaidSoFar }}
              emails={paymentEmails}
              emailsLoading={isLoadingPaymentEmails}
            />
          </div>
        </div>
      </>
    </React.Fragment>
  );
};
